import React, { useState, useEffect } from 'react';

// Simple hash-based router for single-file deployment
const useHashRouter = () => {
  const [route, setRoute] = useState(window.location.hash.slice(1) || '/');
  
  useEffect(() => {
    const handleHashChange = () => {
      setRoute(window.location.hash.slice(1) || '/');
    };
    window.addEventListener('hashchange', handleHashChange);
    return () => window.removeEventListener('hashchange', handleHashChange);
  }, []);
  
  return route;
};

const navigate = (path) => {
  window.location.hash = path;
  window.scrollTo(0, 0);
};

// Shared styles
const sharedStyles = `
  @import url('https://fonts.googleapis.com/css2?family=DM+Sans:ital,wght@0,400;0,500;0,600;0,700;1,400;1,500&family=Playfair+Display:ital,wght@0,400;0,500;0,600;1,400;1,500&display=swap');
  
  * { margin: 0; padding: 0; box-sizing: border-box; }
  
  :root {
    --bg-dark: #070d14;
    --bg-mid: #0a1628;
    --bg-card: rgba(15, 23, 36, 0.9);
    --accent-cyan: #4ECDC4;
    --accent-green: #2ECC71;
    --time-orange: #F39C12;
    --time-purple: #9B59B6;
    --ui-blue: #3498DB;
    --text-primary: #FFFFFF;
    --text-secondary: rgba(255, 255, 255, 0.7);
    --text-muted: rgba(255, 255, 255, 0.4);
    --border-subtle: rgba(255, 255, 255, 0.08);
  }
  
  @keyframes float {
    0%, 100% { transform: translateY(0px); }
    50% { transform: translateY(-20px); }
  }
  
  @keyframes floatReverse {
    0%, 100% { transform: translateY(0px); }
    50% { transform: translateY(-15px); }
  }
  
  @keyframes pulse {
    0%, 100% { opacity: 0.3; transform: scale(1); }
    50% { opacity: 0.5; transform: scale(1.05); }
  }
  
  @keyframes marquee {
    0% { transform: translateX(0); }
    100% { transform: translateX(-50%); }
  }
  
  @keyframes fadeInUp {
    from { opacity: 0; transform: translateY(40px); }
    to { opacity: 1; transform: translateY(0); }
  }
  
  .glow-orb {
    position: absolute;
    border-radius: 50%;
    filter: blur(100px);
    animation: pulse 8s ease-in-out infinite;
    pointer-events: none;
  }
  
  .floating { animation: float 6s ease-in-out infinite; }
  .floating-reverse { animation: floatReverse 5s ease-in-out infinite; }
  .fade-in-up { animation: fadeInUp 0.8s ease-out forwards; }
  
  .cta-button {
    background: linear-gradient(135deg, #4ECDC4 0%, #2ECC71 100%);
    color: #0a1628;
    padding: 16px 32px;
    border-radius: 50px;
    font-weight: 600;
    font-size: 16px;
    border: none;
    cursor: pointer;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    box-shadow: 0 4px 24px rgba(78, 205, 196, 0.3);
    text-decoration: none;
    display: inline-block;
  }
  
  .cta-button:hover {
    transform: translateY(-2px) scale(1.02);
    box-shadow: 0 8px 40px rgba(78, 205, 196, 0.4);
  }
  
  .nav-link {
    color: rgba(255, 255, 255, 0.7);
    text-decoration: none;
    font-size: 14px;
    cursor: pointer;
    transition: color 0.2s;
  }
  
  .nav-link:hover {
    color: #4ECDC4;
  }
  
  .page-container {
    min-height: 100vh;
    background: linear-gradient(180deg, #0d1520 0%, #0a1628 50%, #070d14 100%);
    color: #ffffff;
    font-family: "DM Sans", system-ui, sans-serif;
  }
  
  .content-section {
    max-width: 800px;
    margin: 0 auto;
    padding: 60px 40px 120px;
  }
  
  .content-section h1 {
    font-family: "Playfair Display", serif;
    font-size: 48px;
    font-weight: 500;
    margin-bottom: 16px;
  }
  
  .content-section h2 {
    font-size: 24px;
    font-weight: 600;
    margin-top: 40px;
    margin-bottom: 16px;
    color: #4ECDC4;
  }
  
  .content-section h3 {
    font-size: 18px;
    font-weight: 600;
    margin-top: 24px;
    margin-bottom: 12px;
  }
  
  .content-section p {
    font-size: 16px;
    line-height: 1.8;
    color: rgba(255, 255, 255, 0.7);
    margin-bottom: 16px;
  }
  
  .content-section ul {
    margin: 16px 0;
    padding-left: 24px;
  }
  
  .content-section li {
    font-size: 16px;
    line-height: 1.8;
    color: rgba(255, 255, 255, 0.7);
    margin-bottom: 8px;
  }
  
  .content-section a {
    color: #4ECDC4;
    text-decoration: none;
  }
  
  .content-section a:hover {
    text-decoration: underline;
  }
  
  .last-updated {
    font-size: 14px;
    color: rgba(255, 255, 255, 0.4);
    margin-bottom: 40px;
  }
  
  .contact-card {
    background: rgba(78, 205, 196, 0.1);
    border: 1px solid rgba(78, 205, 196, 0.3);
    border-radius: 16px;
    padding: 24px;
    margin: 32px 0;
  }
  
  .contact-card h3 {
    margin-top: 0;
    color: #4ECDC4;
  }
  
  /* iPhone 15 Pro Mockup */
  .phone-mockup {
    width: 280px;
    height: 572px;
    background: linear-gradient(145deg, #2a2a2c 0%, #1a1a1c 50%, #0a0a0c 100%);
    border-radius: 52px;
    position: relative;
    box-shadow: 
      0 50px 100px rgba(0, 0, 0, 0.6),
      0 25px 60px rgba(0, 0, 0, 0.4),
      inset 0 0 0 1px rgba(255, 255, 255, 0.1);
  }
  
  .phone-mockup::before {
    content: '';
    position: absolute;
    top: -1px; left: -1px; right: -1px; bottom: -1px;
    border-radius: 53px;
    background: linear-gradient(145deg, #4a4a4c 0%, #2a2a2c 20%, #1a1a1c 80%, #3a3a3c 100%);
    z-index: -1;
  }
  
  .phone-mockup::after {
    content: '';
    position: absolute;
    left: -2px;
    top: 120px;
    width: 3px;
    height: 32px;
    background: linear-gradient(180deg, #3a3a3c 0%, #2a2a2c 50%, #3a3a3c 100%);
    border-radius: 2px 0 0 2px;
  }
  
  .phone-screen {
    position: absolute;
    top: 4px; left: 4px; right: 4px; bottom: 4px;
    background: #000;
    border-radius: 48px;
    overflow: hidden;
  }
  
  .phone-screen::before {
    content: '';
    position: absolute;
    top: 11px;
    left: 50%;
    transform: translateX(-50%);
    width: 90px;
    height: 26px;
    background: #000;
    border-radius: 15px;
    z-index: 100;
  }
  
  .phone-screen::after {
    content: '';
    position: absolute;
    top: 17px;
    left: 50%;
    transform: translateX(-50%) translateX(26px);
    width: 8px;
    height: 8px;
    background: radial-gradient(circle at 30% 30%, #1a1a2e 0%, #0a0a12 60%, #000 100%);
    border-radius: 50%;
    z-index: 101;
  }
  
  .marquee-container {
    width: 100%;
    overflow: hidden;
    background: linear-gradient(90deg, #4ECDC4 0%, #2ECC71 100%);
    padding: 14px 0;
  }
  
  .marquee-content {
    display: flex;
    animation: marquee 20s linear infinite;
    white-space: nowrap;
  }
  
  .marquee-item {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 0 40px;
    color: #0a1628;
    font-weight: 600;
    font-size: 15px;
  }
  
  .section {
    padding: 120px 40px;
    max-width: 1400px;
    margin: 0 auto;
  }
  
  .feature-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 80px;
    align-items: center;
  }
  
  .feature-grid.reverse { direction: rtl; }
  .feature-grid.reverse > * { direction: ltr; }
  
  .decorative-pill {
    display: inline-block;
    padding: 8px 16px;
    background: rgba(78, 205, 196, 0.1);
    border: 1px solid rgba(78, 205, 196, 0.25);
    border-radius: 50px;
    font-size: 14px;
    color: #4ECDC4;
  }
  
  .gradient-text {
    background: linear-gradient(135deg, #4ECDC4 0%, #2ECC71 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }
  
  .logo-bar {
    display: flex;
    justify-content: center;
    gap: 48px;
    flex-wrap: wrap;
  }
  
  .card {
    background: rgba(20, 28, 40, 0.85);
    border-radius: 16px;
    padding: 14px;
    margin-bottom: 10px;
  }
  
  @media (max-width: 968px) {
    .feature-grid { grid-template-columns: 1fr; gap: 40px; }
    .section { padding: 60px 20px; }
    .content-section { padding: 40px 20px 80px; }
    .content-section h1 { font-size: 36px; }
  }
`;

// Navigation Component
const Navigation = () => (
  <nav style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '24px 48px', position: 'relative', zIndex: 10 }}>
    <div 
      onClick={() => navigate('/')} 
      style={{ display: 'flex', alignItems: 'center', gap: '12px', cursor: 'pointer' }}
    >
      <div style={{
        width: '40px',
        height: '40px',
        borderRadius: '12px',
        background: 'linear-gradient(135deg, #0a1628 0%, #152238 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        boxShadow: '0 0 20px rgba(78, 205, 196, 0.3)'
      }}>
        <svg width="28" height="28" viewBox="0 0 100 100" fill="none">
          <path d="M50 15 C25 15 15 35 15 50 C15 70 30 85 50 85 C55 85 58 82 60 78 C62 82 65 85 70 85 C80 85 85 75 85 65 C85 55 80 45 75 40 C80 35 82 28 78 22 C74 16 65 15 55 18" stroke="#4ECDC4" strokeWidth="2" fill="none" opacity="0.8"/>
          <circle cx="30" cy="40" r="2" fill="#4ECDC4"/>
          <circle cx="45" cy="30" r="2" fill="#4ECDC4"/>
          <circle cx="60" cy="35" r="2" fill="#4ECDC4"/>
          <circle cx="70" cy="50" r="2" fill="#4ECDC4"/>
          <line x1="30" y1="40" x2="45" y2="30" stroke="#4ECDC4" strokeWidth="1" opacity="0.5"/>
          <line x1="45" y1="30" x2="60" y2="35" stroke="#4ECDC4" strokeWidth="1" opacity="0.5"/>
          <rect x="38" y="38" width="14" height="7" rx="3.5" fill="#4ECDC4" transform="rotate(-30 45 42)"/>
          <rect x="52" y="52" width="14" height="7" rx="3.5" fill="#4ECDC4" transform="rotate(20 59 56)"/>
        </svg>
      </div>
      <span style={{ fontSize: '22px', fontWeight: '700' }}>optivo</span>
    </div>
    <div style={{ display: 'flex', alignItems: 'center', gap: '32px' }}>
      <span className="nav-link" onClick={() => navigate('/support')}>Support</span>
      <span className="nav-link" onClick={() => navigate('/privacy')}>Privacy</span>
      <button className="cta-button" style={{ padding: '12px 24px', fontSize: '14px' }}>Join Beta</button>
    </div>
  </nav>
);

// Footer Component
const Footer = () => (
  <footer style={{ padding: '60px 48px', borderTop: '1px solid rgba(255,255,255,0.08)' }}>
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '24px' }}>
      <div 
        onClick={() => navigate('/')} 
        style={{ display: 'flex', alignItems: 'center', gap: '12px', cursor: 'pointer' }}
      >
        <div style={{ width: '36px', height: '36px', borderRadius: '10px', background: 'linear-gradient(135deg, #0a1628, #152238)', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 0 15px rgba(78, 205, 196, 0.3)' }}>
          <svg width="24" height="24" viewBox="0 0 100 100" fill="none">
            <path d="M50 15 C25 15 15 35 15 50 C15 70 30 85 50 85" stroke="#4ECDC4" strokeWidth="2.5" fill="none" opacity="0.8"/>
            <rect x="38" y="38" width="16" height="8" rx="4" fill="#4ECDC4" transform="rotate(-30 46 42)"/>
            <rect x="52" y="52" width="16" height="8" rx="4" fill="#4ECDC4" transform="rotate(20 60 56)"/>
          </svg>
        </div>
        <span style={{ fontSize: '18px', fontWeight: '600' }}>optivo</span>
      </div>
      <div style={{ display: 'flex', gap: '32px', fontSize: '14px', color: 'rgba(255,255,255,0.5)' }}>
        <span className="nav-link" onClick={() => navigate('/support')}>Support</span>
        <span className="nav-link" onClick={() => navigate('/privacy')}>Privacy Policy</span>
        <a href="#" style={{ color: 'inherit', textDecoration: 'none' }}>Twitter</a>
      </div>
      <div style={{ fontSize: '13px', color: 'rgba(255,255,255,0.3)' }}>
        © 2025 Optivo · Solo Bootstrapped 💊
      </div>
    </div>
  </footer>
);

// Privacy Policy Page
const PrivacyPolicy = () => (
  <div className="page-container">
    <style>{sharedStyles}</style>
    <Navigation />
    
    <div className="content-section">
      <h1>Privacy Policy</h1>
      <p className="last-updated">Last updated: December 21, 2025</p>
      
      <p>
        Optivo ("we", "our", or "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our mobile application.
      </p>
      
      <h2>Information We Collect</h2>
      
      <h3>Information You Provide</h3>
      <p>We may collect information that you voluntarily provide when using Optivo, including:</p>
      <ul>
        <li>Supplement stack information (names, dosages, timing preferences)</li>
        <li>Health and wellness goals</li>
        <li>Wake and sleep schedule preferences</li>
        <li>Meal timing preferences</li>
        <li>Account information (if you create an account)</li>
      </ul>
      
      <h3>Automatically Collected Information</h3>
      <p>When you use our app, we may automatically collect:</p>
      <ul>
        <li>Device information (device type, operating system)</li>
        <li>App usage data and analytics</li>
        <li>Crash reports and performance data</li>
      </ul>
      
      <h2>How We Use Your Information</h2>
      <p>We use the collected information to:</p>
      <ul>
        <li>Provide and optimize supplement scheduling recommendations</li>
        <li>Personalize your experience based on your preferences</li>
        <li>Improve our app's functionality and user experience</li>
        <li>Send you notifications about your supplement schedule (if enabled)</li>
        <li>Respond to your inquiries and support requests</li>
      </ul>
      
      <h2>Data Storage and Security</h2>
      <p>
        Your supplement data is stored locally on your device by default. If you choose to create an account for backup and sync features, your data will be securely stored on our servers using industry-standard encryption.
      </p>
      <p>
        We implement appropriate technical and organizational measures to protect your personal information against unauthorized access, alteration, disclosure, or destruction.
      </p>
      
      <h2>Data Sharing</h2>
      <p>
        We do not sell, trade, or rent your personal information to third parties. We may share anonymized, aggregated data for research or analytical purposes, but this data cannot be used to identify you personally.
      </p>
      
      <h2>Third-Party Services</h2>
      <p>Our app may use third-party services for:</p>
      <ul>
        <li>Analytics (to understand app usage patterns)</li>
        <li>Crash reporting (to improve app stability)</li>
        <li>Cloud storage (if you opt into sync features)</li>
      </ul>
      <p>These services have their own privacy policies governing the use of your information.</p>
      
      <h2>Your Rights</h2>
      <p>You have the right to:</p>
      <ul>
        <li>Access the personal data we hold about you</li>
        <li>Request correction of inaccurate data</li>
        <li>Request deletion of your data</li>
        <li>Export your data in a portable format</li>
        <li>Opt out of analytics and tracking</li>
      </ul>
      
      <h2>Children's Privacy</h2>
      <p>
        Optivo is not intended for use by children under the age of 13. We do not knowingly collect personal information from children under 13. If you believe we have collected information from a child under 13, please contact us immediately.
      </p>
      
      <h2>Changes to This Policy</h2>
      <p>
        We may update this Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page and updating the "Last updated" date.
      </p>
      
      <h2>Contact Us</h2>
      <div className="contact-card">
        <h3>Questions about your privacy?</h3>
        <p style={{ marginBottom: 0 }}>
          If you have any questions about this Privacy Policy or our data practices, please contact us at:
        </p>
        <p style={{ marginBottom: 0 }}>
          <strong>Email:</strong> <a href="mailto:privacy@getoptivo.com">privacy@getoptivo.com</a>
        </p>
      </div>
    </div>
    
    <Footer />
  </div>
);

// Support Page
const Support = () => (
  <div className="page-container">
    <style>{sharedStyles}</style>
    <Navigation />
    
    <div className="content-section">
      <h1>Support</h1>
      <p className="last-updated">We're here to help you optimize your supplement stack.</p>
      
      <div className="contact-card">
        <h3>📧 Contact Us</h3>
        <p>
          For any questions, issues, or feedback, please reach out to us at:
        </p>
        <p style={{ marginBottom: 0 }}>
          <a href="mailto:support@getoptivo.com" style={{ fontSize: '18px', fontWeight: '600' }}>support@getoptivo.com</a>
        </p>
        <p style={{ fontSize: '14px', color: 'rgba(255,255,255,0.5)', marginTop: '8px', marginBottom: 0 }}>
          We typically respond within 24-48 hours.
        </p>
      </div>
      
      <h2>Frequently Asked Questions</h2>
      
      <h3>How does Optivo schedule my supplements?</h3>
      <p>
        Optivo uses a constraint-solving algorithm that considers supplement interactions, absorption requirements, food timing, and your personal schedule to create an optimized daily protocol. Every recommendation is backed by PubMed research.
      </p>
      
      <h3>Where does the supplement data come from?</h3>
      <p>
        Our database contains information on 123+ supplements, with interaction data, timing requirements, and dosage guidelines sourced from peer-reviewed scientific literature and PubMed studies.
      </p>
      
      <h3>Can I customize my schedule?</h3>
      <p>
        Yes! You can set your wake time, sleep time, and meal windows. Optivo will optimize your supplement timing around your personal schedule while respecting all interaction constraints.
      </p>
      
      <h3>What if I add a new supplement?</h3>
      <p>
        When you add a new supplement, Optivo automatically re-optimizes your entire schedule to find the best timing that doesn't conflict with your existing stack.
      </p>
      
      <h3>Is my data secure?</h3>
      <p>
        Your supplement data is stored locally on your device by default. We use industry-standard encryption for any data that's synced to our servers. See our <span className="nav-link" onClick={() => navigate('/privacy')}>Privacy Policy</span> for more details.
      </p>
      
      <h3>How do I delete my data?</h3>
      <p>
        You can delete all your data from within the app's settings. If you need assistance, contact us at <a href="mailto:support@getoptivo.com">support@getoptivo.com</a> and we'll help you with data deletion.
      </p>
      
      <h2>Report a Bug</h2>
      <p>
        Found something that's not working correctly? Please email us at <a href="mailto:support@getoptivo.com">support@getoptivo.com</a> with:
      </p>
      <ul>
        <li>A description of the issue</li>
        <li>Steps to reproduce the problem</li>
        <li>Your device model and iOS version</li>
        <li>Screenshots (if applicable)</li>
      </ul>
      
      <h2>Feature Requests</h2>
      <p>
        We love hearing your ideas! If you have suggestions for new features or improvements, please email us. Your feedback directly shapes our product roadmap.
      </p>
      
      <div className="contact-card" style={{ background: 'rgba(46, 204, 113, 0.1)', borderColor: 'rgba(46, 204, 113, 0.3)' }}>
        <h3 style={{ color: '#2ECC71' }}>💡 Pro Tip</h3>
        <p style={{ marginBottom: 0 }}>
          Join our beta community on Twitter <a href="#">@GetOptivo</a> for the latest updates, tips on optimizing your stack, and early access to new features.
        </p>
      </div>
    </div>
    
    <Footer />
  </div>
);

// Home Page (Main Landing)
const HomePage = () => {
  const [openFaq, setOpenFaq] = useState(null);
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    setIsLoaded(true);
  }, []);

  const faqs = [
    {
      q: "How is Optivo different from pill reminder apps?",
      a: "Pill reminders just buzz at set times. Optivo actually understands supplement science—which ones synergize, which block absorption, what needs an empty stomach vs. fats. It's constraint-solving, not alarm-setting."
    },
    {
      q: "Where does the interaction data come from?",
      a: "Every interaction in our database is backed by PubMed research. We've compiled data on 123+ supplements including timing windows, food requirements, and known interactions. No bro-science."
    },
    {
      q: "What if I change my supplements?",
      a: "The algorithm re-optimizes your entire schedule instantly. Add a new supplement? We'll find the best slot that doesn't conflict with your existing stack."
    }
  ];

  const testimonials = [
    { text: "Finally, an app that understands my stack isn't just 'take pills at random times'", author: "Reddit r/Nootropics" },
    { text: "The interaction warnings alone saved me from wasting months of suboptimal absorption", author: "Biohacker Community" },
    { text: "I spend $300/mo on supplements. This is the first app that makes me feel like I'm not just guessing.", author: "Beta Tester" }
  ];

  const SIZES = {
    iconLarge: 32,
    iconMedium: 28,
    iconSmall: 24,
    cardPadding: 14,
    cardRadius: 16,
    cardGap: 10,
    fontSize: {
      header: 9,
      title: 20,
      subtitle: 11,
      cardTitle: 14,
      cardTime: 11,
      suppName: 13,
      suppDose: 10,
      button: 11,
    }
  };

  const PhoneContent = ({ children, bg = 'linear-gradient(180deg, #080c12 0%, #040608 100%)' }) => (
    <div style={{
      position: 'absolute',
      top: 0, left: 0, right: 0, bottom: 0,
      background: bg,
      padding: '48px 14px 16px',
      overflowY: 'auto',
      overflowX: 'hidden'
    }}>
      {children}
    </div>
  );

  const TimeBlockHeader = ({ icon, iconBg, title, time, timeColor, showTakeAll = false }) => (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <div style={{
          width: SIZES.iconLarge,
          height: SIZES.iconLarge,
          borderRadius: 10,
          background: iconBg,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: 16
        }}>{icon}</div>
        <div>
          <div style={{ fontSize: SIZES.fontSize.cardTitle, fontWeight: 600 }}>{title}</div>
          <div style={{ fontSize: SIZES.fontSize.cardTime, color: timeColor }}>{time}</div>
        </div>
      </div>
      {showTakeAll && (
        <div style={{
          fontSize: SIZES.fontSize.button,
          background: '#4ECDC4',
          color: '#0a1628',
          padding: '6px 14px',
          borderRadius: 14,
          fontWeight: 600
        }}>Take All</div>
      )}
    </div>
  );

  const SupplementRow = ({ color, name, dose, time, showActions = false }) => (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: '6px 0'
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <div style={{
          width: SIZES.iconMedium,
          height: SIZES.iconMedium,
          borderRadius: '50%',
          background: color
        }} />
        <div>
          <div style={{ fontSize: SIZES.fontSize.suppName, fontWeight: 500 }}>{name}</div>
          <div style={{ fontSize: SIZES.fontSize.suppDose, color: 'rgba(255,255,255,0.4)' }}>
            {dose}{time && ` · ${time}`}
          </div>
        </div>
      </div>
      {showActions && (
        <div style={{ display: 'flex', gap: 6 }}>
          <div style={{
            width: 22, height: 22, borderRadius: '50%',
            border: '1px solid rgba(255,255,255,0.2)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 10, color: 'rgba(255,255,255,0.4)'
          }}>?</div>
          <div style={{
            width: 22, height: 22, borderRadius: '50%',
            border: '1px solid #4ECDC4',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 14, color: '#4ECDC4'
          }}>+</div>
        </div>
      )}
    </div>
  );

  return (
    <div className="page-container" style={{ overflow: 'hidden', position: 'relative' }}>
      <style>{sharedStyles}</style>

      {/* Glow Orbs */}
      <div className="glow-orb" style={{ width: '600px', height: '600px', background: 'rgba(78, 205, 196, 0.12)', top: '-200px', right: '-200px' }} />
      <div className="glow-orb" style={{ width: '500px', height: '500px', background: 'rgba(46, 204, 113, 0.08)', bottom: '20%', left: '-150px', animationDelay: '2s' }} />

      <Navigation />

      {/* Hero Section */}
      <section style={{ textAlign: 'center', padding: '80px 40px 120px', position: 'relative', overflow: 'hidden' }}>
        
        {/* Floating Biohacker Icons */}
        <div className="floating" style={{ position: 'absolute', top: '15%', left: '8%', fontSize: '32px', opacity: 0.6, animationDuration: '7s' }}>🧬</div>
        <div className="floating-reverse" style={{ position: 'absolute', top: '25%', right: '10%', fontSize: '28px', opacity: 0.5, animationDuration: '6s' }}>🧠</div>
        <div className="floating" style={{ position: 'absolute', top: '45%', left: '5%', fontSize: '24px', opacity: 0.4, animationDuration: '8s', animationDelay: '1s' }}>💊</div>
        <div className="floating-reverse" style={{ position: 'absolute', top: '60%', right: '7%', fontSize: '26px', opacity: 0.5, animationDuration: '7s', animationDelay: '0.5s' }}>⚡</div>
        <div className="floating" style={{ position: 'absolute', top: '35%', left: '12%', fontSize: '22px', opacity: 0.35, animationDuration: '9s', animationDelay: '2s' }}>🔬</div>
        <div className="floating-reverse" style={{ position: 'absolute', top: '20%', right: '15%', fontSize: '20px', opacity: 0.4, animationDuration: '6.5s', animationDelay: '1.5s' }}>⏱️</div>
        <div className="floating" style={{ position: 'absolute', top: '55%', right: '12%', fontSize: '24px', opacity: 0.45, animationDuration: '8s', animationDelay: '0.8s' }}>🧪</div>
        <div className="floating-reverse" style={{ position: 'absolute', top: '70%', left: '10%', fontSize: '22px', opacity: 0.35, animationDuration: '7.5s', animationDelay: '1.2s' }}>💉</div>
        <div className="floating" style={{ position: 'absolute', top: '40%', right: '5%', fontSize: '18px', opacity: 0.3, animationDuration: '9s', animationDelay: '2.5s' }}>🩸</div>
        <div className="floating-reverse" style={{ position: 'absolute', top: '80%', right: '18%', fontSize: '20px', opacity: 0.4, animationDuration: '6s', animationDelay: '0.3s' }}>📊</div>
        <div className="floating" style={{ position: 'absolute', top: '12%', left: '18%', fontSize: '18px', opacity: 0.35, animationDuration: '8.5s', animationDelay: '1.8s' }}>🎯</div>
        <div className="floating-reverse" style={{ position: 'absolute', top: '65%', left: '15%', fontSize: '20px', opacity: 0.4, animationDuration: '7s', animationDelay: '0.7s' }}>☀️</div>
        <div className="floating" style={{ position: 'absolute', top: '30%', right: '20%', fontSize: '22px', opacity: 0.35, animationDuration: '8s', animationDelay: '2.2s' }}>🌙</div>
        <div className="floating-reverse" style={{ position: 'absolute', top: '75%', left: '20%', fontSize: '18px', opacity: 0.3, animationDuration: '6.5s', animationDelay: '1s' }}>💪</div>

        <div style={{ maxWidth: '900px', margin: '0 auto', position: 'relative', zIndex: 1 }}>
          <div className={isLoaded ? 'fade-in-up' : ''} style={{ opacity: isLoaded ? 1 : 0, animationDelay: '0.1s' }}>
            <span className="decorative-pill" style={{ marginBottom: '32px' }}>🧬 Constraint-Based Scheduling</span>
          </div>
          
          <h1 className={isLoaded ? 'fade-in-up' : ''} style={{
            fontFamily: '"Playfair Display", serif',
            fontSize: 'clamp(48px, 8vw, 80px)',
            fontWeight: '500',
            lineHeight: 1.1,
            marginBottom: '24px',
            marginTop: '24px',
            opacity: isLoaded ? 1 : 0,
            animationDelay: '0.2s'
          }}>
            Your supplements.<br />
            <em style={{ fontStyle: 'italic' }} className="gradient-text">Actually optimized.</em>
          </h1>
          
          <p className={isLoaded ? 'fade-in-up' : ''} style={{
            fontSize: '20px',
            color: 'rgba(255, 255, 255, 0.6)',
            maxWidth: '600px',
            margin: '0 auto 40px',
            lineHeight: 1.6,
            opacity: isLoaded ? 1 : 0,
            animationDelay: '0.3s'
          }}>
            Stop guessing when to take your stack. Optivo uses constraint-solving algorithms to optimize timing, avoid interactions, and maximize absorption.
          </p>
          
          <div className={isLoaded ? 'fade-in-up' : ''} style={{ display: 'flex', gap: '16px', justifyContent: 'center', flexWrap: 'wrap', marginBottom: '24px', opacity: isLoaded ? 1 : 0, animationDelay: '0.4s' }}>
            <button className="cta-button">Join the Beta Waitlist</button>
            <button className="cta-button" style={{ background: 'rgba(78, 205, 196, 0.1)', color: '#4ECDC4', border: '1px solid rgba(78, 205, 196, 0.3)', boxShadow: 'none' }}>See How It Works</button>
          </div>
          
          <div className={isLoaded ? 'fade-in-up' : ''} style={{ display: 'flex', gap: '24px', justifyContent: 'center', fontSize: '14px', color: 'rgba(255, 255, 255, 0.5)', opacity: isLoaded ? 1 : 0, animationDelay: '0.5s' }}>
            <span>✓ 123+ Supplements</span>
            <span>✓ PubMed-Backed Data</span>
            <span>✓ Built in 2 Weeks</span>
          </div>

          {/* Hero Phone Mockup */}
          <div style={{ marginTop: '60px', display: 'flex', justifyContent: 'center' }}>
            <div className="phone-mockup floating" style={{ animationDuration: '8s' }}>
              <div className="phone-screen">
                <PhoneContent>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 14 }}>
                    <div>
                      <div style={{ fontSize: 9, color: '#F39C12', fontWeight: 600, letterSpacing: 1.5, marginBottom: 2 }}>DAILY PROTOCOL</div>
                      <div style={{ fontSize: 22, fontWeight: 700, letterSpacing: -0.5 }}>Today</div>
                      <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.4)' }}>Wednesday, December 17</div>
                    </div>
                    <div style={{
                      background: 'rgba(78, 205, 196, 0.1)',
                      border: '1px solid rgba(78, 205, 196, 0.3)',
                      borderRadius: 16,
                      padding: '5px 10px',
                      fontSize: 10,
                      color: '#4ECDC4',
                      fontWeight: 500,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 4
                    }}>
                      <span style={{ fontSize: 12 }}>⊕</span> Check-in
                    </div>
                  </div>

                  <div style={{ marginBottom: 16 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 6 }}>
                      <div>
                        <span style={{ fontSize: 28, fontWeight: 700 }}>0</span>
                        <span style={{ fontSize: 14, color: 'rgba(255,255,255,0.3)' }}>/5</span>
                        <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.4)', marginLeft: 6 }}>supplements</span>
                      </div>
                      <div style={{ fontSize: 12, color: '#4ECDC4', fontWeight: 500 }}>0%</div>
                    </div>
                    <div style={{ height: 3, background: 'rgba(255,255,255,0.1)', borderRadius: 2 }}>
                      <div style={{ height: '100%', width: '0%', background: 'linear-gradient(90deg, #4ECDC4, #2ECC71)', borderRadius: 2 }} />
                    </div>
                  </div>

                  <div className="card" style={{ border: '1px solid #F39C12' }}>
                    <TimeBlockHeader icon="☀️" iconBg="linear-gradient(135deg, #F39C12, #E67E22)" title="Wake Up" time="6:30 AM" timeColor="#F39C12" />
                    <SupplementRow color="linear-gradient(135deg, #9B59B6, #8E44AD)" name="Zinc" dose="30 mg" time="6:30 AM" showActions />
                  </div>

                  <div className="card" style={{ border: '1px solid #F39C12' }}>
                    <TimeBlockHeader icon="🥣" iconBg="rgba(243, 156, 18, 0.15)" title="Breakfast" time="7:30 AM" timeColor="#F39C12" showTakeAll />
                    <SupplementRow color="#F39C12" name="Vitamin D3" dose="5000 IU" time="7:30 AM" showActions />
                    <SupplementRow color="#2ECC71" name="Ashwagandha (KSM-66)" dose="600 mg" time="8:00 AM" showActions />
                  </div>

                  <div className="card" style={{ border: '1px solid #9B59B6' }}>
                    <TimeBlockHeader icon="🌙" iconBg="rgba(155, 89, 182, 0.15)" title="Before Bed" time="10:30 PM" timeColor="#BB8FCE" showTakeAll />
                  </div>
                </PhoneContent>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Built For Section */}
      <section style={{ padding: '60px 40px', textAlign: 'center' }}>
        <p style={{ fontSize: '13px', textTransform: 'uppercase', letterSpacing: '3px', color: 'rgba(255,255,255,0.4)', marginBottom: '32px' }}>Built for serious optimizers</p>
        <div className="logo-bar" style={{ opacity: 0.7 }}>
          <span style={{ fontSize: '16px', fontWeight: '500' }}>🧬 Biohackers</span>
          <span style={{ fontSize: '16px', fontWeight: '500' }}>🧠 Nootropics Users</span>
          <span style={{ fontSize: '16px', fontWeight: '500' }}>💪 Athletes</span>
          <span style={{ fontSize: '16px', fontWeight: '500' }}>💰 $200+/mo Stacks</span>
        </div>
      </section>

      {/* ========== SECTION 1 - Settings / Circadian Rhythm ========== */}
      <section className="section">
        <div className="feature-grid">
          <div>
            <span className="decorative-pill" style={{ marginBottom: '24px' }}>🚫 The Problem</span>
            <h2 style={{ fontFamily: '"Playfair Display", serif', fontSize: 'clamp(36px, 5vw, 52px)', fontWeight: '500', lineHeight: 1.2, marginBottom: '24px', marginTop: '16px' }}>
              Pill reminders are <em style={{ fontStyle: 'italic', color: '#4ECDC4' }}>not</em> enough
            </h2>
            <p style={{ fontSize: '18px', color: 'rgba(255,255,255,0.6)', lineHeight: 1.7, marginBottom: '32px' }}>
              Most supplement apps are glorified alarms. They don't understand interactions, absorption windows, or that magnesium blocks other minerals.
            </p>
            <button className="cta-button">See the Difference</button>
          </div>
          <div style={{ display: 'flex', justifyContent: 'center' }}>
            <div className="phone-mockup floating-reverse">
              <div className="phone-screen">
                <PhoneContent>
                  {/* Settings Header */}
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
                    <div style={{ fontSize: 12, color: '#4ECDC4', fontWeight: 500 }}>← Back</div>
                    <div style={{ fontSize: 15, fontWeight: 600, color: '#fff' }}>Settings</div>
                    <div style={{ width: 40 }} />
                  </div>

                  {/* Circadian Rhythm Section */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                    <span style={{ fontSize: 14 }}>☀️</span>
                    <span style={{ fontSize: 10, color: '#F39C12', fontWeight: 600, letterSpacing: 1.2 }}>CIRCADIAN RHYTHM</span>
                  </div>

                  <div style={{
                    background: 'rgba(255,255,255,0.03)',
                    border: '1px solid rgba(255,255,255,0.08)',
                    borderRadius: 16,
                    padding: 12,
                    marginBottom: 18
                  }}>
                    {/* Wake Time */}
                    <div style={{
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                      padding: '10px 0', borderBottom: '1px solid rgba(255,255,255,0.06)'
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <div style={{
                          width: 32, height: 32, borderRadius: 10,
                          background: 'linear-gradient(135deg, #F39C12, #E67E22)',
                          display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14
                        }}>☀️</div>
                        <span style={{ fontSize: 13, fontWeight: 500 }}>Wake Time</span>
                      </div>
                      <div style={{
                        background: 'rgba(243,156,18,0.2)',
                        padding: '6px 14px', borderRadius: 8,
                        fontSize: 12, color: '#F39C12', fontWeight: 600
                      }}>6:30 AM</div>
                    </div>
                    {/* Sleep Time */}
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 0' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <div style={{
                          width: 32, height: 32, borderRadius: 10,
                          background: 'linear-gradient(135deg, #9B59B6, #8E44AD)',
                          display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14
                        }}>🌙</div>
                        <span style={{ fontSize: 13, fontWeight: 500 }}>Sleep Time</span>
                      </div>
                      <div style={{
                        background: 'rgba(155,89,182,0.2)',
                        padding: '6px 14px', borderRadius: 8,
                        fontSize: 12, color: '#BB8FCE', fontWeight: 600
                      }}>10:30 PM</div>
                    </div>
                  </div>

                  {/* Nutrition Windows Section */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                    <span style={{ fontSize: 14 }}>🍽️</span>
                    <span style={{ fontSize: 10, color: '#2ECC71', fontWeight: 600, letterSpacing: 1.2 }}>NUTRITION WINDOWS</span>
                  </div>

                  <div style={{
                    background: 'rgba(255,255,255,0.03)',
                    border: '1px solid rgba(255,255,255,0.08)',
                    borderRadius: 16,
                    padding: 12
                  }}>
                    {[
                      { icon: '🍳', name: 'Breakfast', time: '7:30 AM', color: '#2ECC71' },
                      { icon: '🍴', name: 'Lunch', time: '12:30 PM', color: '#4ECDC4' },
                      { icon: '🌙', name: 'Dinner', time: '6:30 PM', color: '#F39C12' }
                    ].map((meal, i, arr) => (
                      <div key={i} style={{
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                        padding: '10px 0',
                        borderBottom: i < arr.length - 1 ? '1px solid rgba(255,255,255,0.06)' : 'none'
                      }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                          <div style={{
                            width: 32, height: 32, borderRadius: 10,
                            background: 'rgba(255,255,255,0.05)',
                            border: '1px solid rgba(255,255,255,0.1)',
                            display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14
                          }}>{meal.icon}</div>
                          <span style={{ fontSize: 13, fontWeight: 500 }}>{meal.name}</span>
                        </div>
                        <div style={{
                          background: `${meal.color}20`,
                          padding: '6px 14px', borderRadius: 8,
                          fontSize: 12, color: meal.color, fontWeight: 600
                        }}>{meal.time}</div>
                      </div>
                    ))}
                  </div>
                </PhoneContent>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ========== SECTION 2 - Vitamin D3 Detail ========== */}
      <section className="section">
        <div className="feature-grid reverse">
          <div>
            <span className="decorative-pill" style={{ marginBottom: '24px' }}>🧮 Constraint Solver</span>
            <h2 style={{ fontFamily: '"Playfair Display", serif', fontSize: 'clamp(36px, 5vw, 52px)', fontWeight: '500', lineHeight: 1.2, marginBottom: '24px', marginTop: '16px' }}>
              Smart scheduling that <em style={{ fontStyle: 'italic', color: '#4ECDC4' }}>actually works</em>
            </h2>
            <p style={{ fontSize: '18px', color: 'rgba(255,255,255,0.6)', lineHeight: 1.7, marginBottom: '32px' }}>
              Optivo uses constraint satisfaction algorithms to optimize your entire stack. It knows which supplements synergize, which block each other, and when to take each one.
            </p>
            <button className="cta-button">Try the Beta</button>
          </div>
          <div style={{ display: 'flex', justifyContent: 'center' }}>
            <div className="phone-mockup floating" style={{ animationDelay: '1s' }}>
              <div className="phone-screen">
                <PhoneContent>
                  {/* Back */}
                  <div style={{ fontSize: 12, color: '#4ECDC4', fontWeight: 500, marginBottom: 16 }}>← Back</div>

                  {/* Supplement Header Card */}
                  <div style={{
                    background: 'rgba(255,255,255,0.03)',
                    border: '1px solid rgba(255,255,255,0.1)',
                    borderTop: '3px solid #F39C12',
                    borderRadius: 16,
                    padding: 16,
                    marginBottom: 12
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14 }}>
                      <div style={{
                        width: 44, height: 44, borderRadius: 12, background: '#F39C12',
                        display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20
                      }}>💊</div>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 16, fontWeight: 600 }}>Vitamin D3</div>
                        <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.4)' }}>Vitamin</div>
                      </div>
                      <div style={{
                        fontSize: 10, background: '#2ECC71', color: '#0a1628',
                        padding: '4px 10px', borderRadius: 12, fontWeight: 600
                      }}>Active</div>
                    </div>

                    {/* Dosage Pills */}
                    <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
                      <div style={{
                        flex: 1, background: 'rgba(78,205,196,0.1)', border: '1px solid rgba(78,205,196,0.3)',
                        borderRadius: 10, padding: '8px 12px', fontSize: 12, color: '#4ECDC4', textAlign: 'center'
                      }}>⚖️ 5000 IU</div>
                      <div style={{
                        flex: 1, background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)',
                        borderRadius: 10, padding: '8px 12px', fontSize: 12, textAlign: 'center'
                      }}>🔄 1x daily</div>
                    </div>

                    {/* Schedule Info */}
                    <div style={{
                      background: 'rgba(78,205,196,0.05)', borderRadius: 10, padding: 12, marginBottom: 12
                    }}>
                      <div style={{ fontSize: 11, color: '#4ECDC4', marginBottom: 4, fontWeight: 500 }}>⏰ Scheduled at 7:30 AM</div>
                      <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.5)', lineHeight: 1.5 }}>
                        Vitamin D is fat-soluble - studies show absorption increases by up to 50% when taken with dietary fat.
                      </div>
                    </div>

                    <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.4)' }}>
                      ℹ️ Essential vitamin for bone health, immune function, and mood regulation.
                    </div>
                  </div>

                  {/* Timing Rules Card */}
                  <div style={{
                    background: 'rgba(255,255,255,0.03)',
                    border: '1px solid #4ECDC4',
                    borderRadius: 16,
                    padding: 14
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
                      <div style={{
                        width: 24, height: 24, borderRadius: '50%', background: '#4ECDC4',
                        display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, color: '#0a1628'
                      }}>⏱</div>
                      <div style={{ fontSize: 13, fontWeight: 600 }}>Timing Rules</div>
                    </div>
                    <div style={{ background: 'rgba(255,255,255,0.03)', borderRadius: 10, padding: 12 }}>
                      <div style={{ fontSize: 12, fontWeight: 500, marginBottom: 6 }}>Fat-soluble - requires dietary fat</div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <div style={{ width: 6, height: 6, borderRadius: '50%', background: '#2ECC71' }} />
                        <div style={{ fontSize: 10, color: '#2ECC71' }}>Strong Clinical Evidence</div>
                      </div>
                    </div>
                  </div>
                </PhoneContent>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ========== SECTION 3 - My Stack ========== */}
      <section className="section">
        <div className="feature-grid">
          <div>
            <span className="decorative-pill" style={{ marginBottom: '24px' }}>📚 PubMed Data</span>
            <h2 style={{ fontFamily: '"Playfair Display", serif', fontSize: 'clamp(36px, 5vw, 52px)', fontWeight: '500', lineHeight: 1.2, marginBottom: '24px', marginTop: '16px' }}>
              <em className="gradient-text" style={{ fontStyle: 'italic' }}>123+ supplements</em> with real data
            </h2>
            <p style={{ fontSize: '18px', color: 'rgba(255,255,255,0.6)', lineHeight: 1.7, marginBottom: '32px' }}>
              Every interaction, timing window, and food requirement is backed by PubMed research. Not opinions. Not bro-science. Actual studies.
            </p>
            <button className="cta-button">Explore the Database</button>
          </div>
          <div style={{ display: 'flex', justifyContent: 'center' }}>
            <div className="phone-mockup floating-reverse" style={{ animationDelay: '0.5s' }}>
              <div className="phone-screen">
                <PhoneContent>
                  {/* Header with FAB */}
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 }}>
                    <div>
                      <div style={{ fontSize: 9, color: '#4ECDC4', fontWeight: 600, letterSpacing: 1.5, marginBottom: 2 }}>SUPPLEMENT ARSENAL</div>
                      <div style={{ fontSize: 20, fontWeight: 700, letterSpacing: -0.5 }}>My Stack</div>
                      <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.4)' }}>5 active supplements</div>
                    </div>
                    <div style={{
                      width: 40, height: 40, borderRadius: '50%',
                      background: 'linear-gradient(135deg, #3498DB, #2980B9)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: 22, color: '#fff', fontWeight: 300,
                      boxShadow: '0 4px 15px rgba(52, 152, 219, 0.5)'
                    }}>+</div>
                  </div>

                  {/* Search Bar */}
                  <div style={{
                    background: 'rgba(255,255,255,0.05)',
                    borderRadius: 12,
                    padding: '11px 14px',
                    marginBottom: 14,
                    fontSize: 12,
                    color: 'rgba(255,255,255,0.35)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8
                  }}>
                    <span>🔍</span> Search supplements...
                  </div>

                  {/* Filter Pills */}
                  <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
                    <div style={{
                      background: '#3498DB', color: '#fff',
                      padding: '7px 14px', borderRadius: 20, fontSize: 11, fontWeight: 600
                    }}>All</div>
                    <div style={{
                      background: 'transparent', border: '1px solid rgba(255,255,255,0.15)',
                      color: 'rgba(255,255,255,0.6)',
                      padding: '7px 12px', borderRadius: 20, fontSize: 11
                    }}>Focus & Cognition</div>
                    <div style={{
                      background: 'rgba(243,156,18,0.12)', border: '1px solid rgba(243,156,18,0.4)',
                      color: '#F39C12',
                      padding: '7px 12px', borderRadius: 20, fontSize: 11
                    }}>Energy</div>
                  </div>

                  {/* Section Header */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
                    <div style={{ width: 3, height: 16, background: '#3498DB', borderRadius: 2 }} />
                    <div style={{ fontSize: 13, fontWeight: 600 }}>Active Supplements</div>
                    <div style={{
                      background: 'rgba(52, 152, 219, 0.2)', color: '#3498DB',
                      padding: '3px 10px', borderRadius: 12, fontSize: 11, fontWeight: 600
                    }}>5</div>
                  </div>

                  {/* Supplement List */}
                  {[
                    { name: 'Vitamin D3', dose: '5000 IU', color: '#F39C12' },
                    { name: 'Magnesium Glycinate', dose: '400 mg', color: '#9B59B6' },
                    { name: 'Melatonin', dose: '0.5 mg', color: '#E74C3C' },
                    { name: 'Ashwagandha', dose: '600 mg', color: '#2ECC71' },
                    { name: 'Zinc', dose: '30 mg', color: '#F5B041' }
                  ].map((supp, i) => (
                    <div key={i} style={{
                      background: 'rgba(20, 28, 40, 0.9)',
                      border: '1px solid rgba(255,255,255,0.06)',
                      borderRadius: 14,
                      padding: '11px 12px',
                      marginBottom: 8,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 10
                    }}>
                      <div style={{
                        width: 24, height: 24, borderRadius: 7,
                        background: 'rgba(52, 152, 219, 0.12)',
                        border: '1.5px solid #3498DB',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: 11, color: '#3498DB'
                      }}>✓</div>
                      <div style={{
                        width: 34, height: 34, borderRadius: 10, background: supp.color
                      }} />
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 1 }}>{supp.name}</div>
                        <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.4)' }}>{supp.dose}</div>
                      </div>
                      <div style={{ fontSize: 18, color: 'rgba(255,255,255,0.2)', fontWeight: 300 }}>›</div>
                    </div>
                  ))}
                </PhoneContent>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ========== SECTION 4 - Add Supplement Modal ========== */}
      <section className="section">
        <div className="feature-grid reverse">
          <div>
            <span className="decorative-pill" style={{ marginBottom: '24px' }}>⚡ Smart Timing</span>
            <h2 style={{ fontFamily: '"Playfair Display", serif', fontSize: 'clamp(36px, 5vw, 52px)', fontWeight: '500', lineHeight: 1.2, marginBottom: '24px', marginTop: '16px' }}>
              Warnings that <em style={{ fontStyle: 'italic', color: '#4ECDC4' }}>prevent</em> conflicts
            </h2>
            <p style={{ fontSize: '18px', color: 'rgba(255,255,255,0.6)', lineHeight: 1.7, marginBottom: '32px' }}>
              Adding Magnesium? Optivo knows it blocks morning minerals and suggests evening instead. Every recommendation is evidence-backed.
            </p>
            <button className="cta-button">Join the Waitlist</button>
          </div>
          <div style={{ display: 'flex', justifyContent: 'center' }}>
            <div className="phone-mockup floating" style={{ animationDelay: '0.3s' }}>
              <div className="phone-screen">
                <PhoneContent>
                  {/* Modal Header */}
                  <div style={{ textAlign: 'center', marginBottom: 16 }}>
                    <div style={{ fontSize: 15, color: '#4ECDC4', fontWeight: 600 }}>Add Supplement</div>
                  </div>

                  {/* Modal Card */}
                  <div style={{
                    background: 'rgba(255,255,255,0.03)',
                    border: '1px solid rgba(78,205,196,0.3)',
                    borderRadius: 20,
                    padding: 18,
                    position: 'relative'
                  }}>
                    {/* Decorative sparkles */}
                    <div style={{ position: 'absolute', top: 10, left: 14, fontSize: 10, color: 'rgba(78,205,196,0.4)' }}>✦</div>
                    <div style={{ position: 'absolute', top: 10, right: 14, fontSize: 10, color: 'rgba(78,205,196,0.4)' }}>✦</div>

                    {/* Icon */}
                    <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 12 }}>
                      <div style={{
                        width: 44, height: 44, borderRadius: '50%',
                        background: 'linear-gradient(135deg, #9B59B6, #8E44AD)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: 20
                      }}>💊</div>
                    </div>

                    {/* Supplement Name */}
                    <div style={{ textAlign: 'center', marginBottom: 16 }}>
                      <div style={{ fontSize: 16, fontWeight: 600 }}>Magnesium L-Threonate</div>
                      <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.4)' }}>2000 mg per serving</div>
                    </div>

                    {/* Frequency */}
                    <div style={{ marginBottom: 14 }}>
                      <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.4)', marginBottom: 8 }}>🔄 Daily Frequency</div>
                      <div style={{ display: 'flex', gap: 8 }}>
                        {[
                          { label: '1x', sub: 'Daily', active: true },
                          { label: '2x', sub: 'Twice', active: false },
                          { label: '3x', sub: '3x Daily', active: false }
                        ].map((opt, i) => (
                          <div key={i} style={{
                            flex: 1,
                            background: opt.active ? 'rgba(78,205,196,0.1)' : 'rgba(255,255,255,0.03)',
                            border: opt.active ? '1px solid #4ECDC4' : '1px solid rgba(255,255,255,0.1)',
                            borderRadius: 10, padding: 10, textAlign: 'center'
                          }}>
                            <div style={{ fontSize: 16, fontWeight: 600, color: opt.active ? '#4ECDC4' : '#fff' }}>{opt.label}</div>
                            <div style={{ fontSize: 9, color: opt.active ? '#4ECDC4' : 'rgba(255,255,255,0.4)' }}>{opt.sub}</div>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Warning */}
                    <div style={{
                      background: 'rgba(243,156,18,0.1)',
                      border: '1px solid rgba(243,156,18,0.3)',
                      borderRadius: 10, padding: 10, marginBottom: 14
                    }}>
                      <div style={{ fontSize: 11, color: '#F39C12', display: 'flex', alignItems: 'center', gap: 6 }}>
                        ⚠️ Best taken in evening/night for optimal effect
                      </div>
                    </div>

                    {/* Time Windows */}
                    <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
                      <div style={{
                        flex: 1, background: 'rgba(255,255,255,0.02)',
                        border: '1px solid rgba(255,255,255,0.08)',
                        borderRadius: 10, padding: 10, textAlign: 'center', opacity: 0.5
                      }}>
                        <div style={{ fontSize: 16, marginBottom: 2 }}>☀️</div>
                        <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.4)' }}>Morning</div>
                        <div style={{ fontSize: 9, color: 'rgba(255,255,255,0.25)' }}>Blocked</div>
                      </div>
                      <div style={{
                        flex: 1, background: 'rgba(243,156,18,0.1)',
                        border: '1px solid #F39C12',
                        borderRadius: 10, padding: 10, textAlign: 'center'
                      }}>
                        <div style={{ fontSize: 16, marginBottom: 2 }}>🌅</div>
                        <div style={{ fontSize: 10, color: '#F39C12' }}>Evening</div>
                        <div style={{ fontSize: 9, color: 'rgba(255,255,255,0.4)' }}>Suggested</div>
                      </div>
                      <div style={{
                        flex: 1, background: 'rgba(255,255,255,0.02)',
                        border: '1px solid rgba(255,255,255,0.08)',
                        borderRadius: 10, padding: 10, textAlign: 'center'
                      }}>
                        <div style={{ fontSize: 16, marginBottom: 2 }}>🌙</div>
                        <div style={{ fontSize: 10 }}>Night</div>
                        <div style={{ fontSize: 9, color: 'rgba(255,255,255,0.4)' }}>Available</div>
                      </div>
                    </div>

                    {/* CTA Button */}
                    <div style={{
                      background: 'linear-gradient(90deg, #4ECDC4, #9B59B6)',
                      borderRadius: 12, padding: 14, textAlign: 'center',
                      fontWeight: 600, fontSize: 14, color: '#0a1628'
                    }}>
                      Add to Stack
                    </div>
                  </div>
                </PhoneContent>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Marquee */}
      <div className="marquee-container">
        <div className="marquee-content">
          {[...Array(8)].map((_, i) => (
            <div key={i} className="marquee-item">
              <span>💊</span>
              <span>Stop wasting your supplements</span>
            </div>
          ))}
        </div>
      </div>

      {/* Testimonials */}
      <section className="section" style={{ textAlign: 'center' }}>
        <h2 style={{ fontFamily: '"Playfair Display", serif', fontSize: 'clamp(36px, 5vw, 48px)', fontWeight: '500', marginBottom: '60px' }}>
          Built for Optimizers <span style={{ color: '#4ECDC4' }}>⚡</span>
        </h2>
        
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '24px', maxWidth: '1000px', margin: '0 auto' }}>
          {testimonials.map((t, i) => (
            <div key={i} style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '24px', padding: '32px', textAlign: 'left' }}>
              <p style={{ fontSize: '18px', lineHeight: 1.6, marginBottom: '20px', color: 'rgba(255,255,255,0.9)' }}>"{t.text}"</p>
              <p style={{ fontSize: '14px', color: '#4ECDC4' }}>— {t.author}</p>
            </div>
          ))}
        </div>
      </section>

      {/* FAQ */}
      <section className="section">
        <h2 style={{ fontFamily: '"Playfair Display", serif', fontSize: 'clamp(36px, 5vw, 48px)', fontWeight: '500', textAlign: 'center', marginBottom: '60px' }}>
          Questions? <em style={{ fontStyle: 'italic', color: '#4ECDC4' }}>Answered.</em>
        </h2>
        
        <div style={{ maxWidth: '700px', margin: '0 auto' }}>
          {faqs.map((faq, i) => (
            <div key={i} style={{ borderBottom: '1px solid rgba(255,255,255,0.1)', padding: '24px 0' }}>
              <button onClick={() => setOpenFaq(openFaq === i ? null : i)} style={{ width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: 'none', border: 'none', color: '#fff', fontSize: '18px', fontWeight: '500', cursor: 'pointer', textAlign: 'left' }}>
                {faq.q}
                <span style={{ fontSize: '24px', transform: openFaq === i ? 'rotate(45deg)' : 'none', transition: 'transform 0.3s' }}>+</span>
              </button>
              {openFaq === i && (
                <p style={{ marginTop: '16px', color: 'rgba(255,255,255,0.6)', lineHeight: 1.7 }}>{faq.a}</p>
              )}
            </div>
          ))}
        </div>
      </section>

      {/* CTA Section */}
      <section className="section" style={{ textAlign: 'center' }}>
        <h2 style={{ fontFamily: '"Playfair Display", serif', fontSize: 'clamp(36px, 5vw, 52px)', fontWeight: '500', marginBottom: '24px' }}>
          Join the <em className="gradient-text" style={{ fontStyle: 'italic' }}>Optivo</em> Beta
        </h2>
        <p style={{ fontSize: '18px', color: 'rgba(255,255,255,0.6)', marginBottom: '40px', maxWidth: '500px', margin: '0 auto 40px' }}>
          Stop wasting money on suboptimal timing. Get your stack optimized.
        </p>
        <button className="cta-button" style={{ fontSize: '18px', padding: '20px 48px' }}>Join the Waitlist</button>
        <div style={{ marginTop: '24px', fontSize: '14px', color: 'rgba(255,255,255,0.4)' }}>
          ✓ 123+ Supplements · ✓ PubMed Data · ✓ Launching Soon
        </div>
      </section>

      <Footer />
    </div>
  );
};

// Main App with Router
export default function OptivoApp() {
  const route = useHashRouter();
  
  switch (route) {
    case '/privacy':
      return <PrivacyPolicy />;
    case '/support':
      return <Support />;
    default:
      return <HomePage />;
  }
}
